import { rtkApi, GET_NOTIFICATIONS, GET_NOTIFICATIONS_UNREAD_COUNT } from '@/shared/api';
import { SuccessApiResponse } from '@/shared/types/common';
import type {
  INotificationsListRequest,
  INotificationsListResponse,
  INotificationsUnreadCountResponse,
} from './types';

const notificationsApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getUnreadNotificationsCount: build.query<INotificationsUnreadCountResponse, void>({
      query: () => '/notifications/unread-count',
      transformResponse: (response: SuccessApiResponse<INotificationsUnreadCountResponse>) =>
        response.data,
      providesTags: [GET_NOTIFICATIONS_UNREAD_COUNT],
    }),
    getNotifications: build.query<INotificationsListResponse, INotificationsListRequest>({
      query: (params) => ({
        url: '/notifications',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 8,
          search: params.search || undefined,
          sort: params.sort ?? 'createdAt:desc',
        },
      }),
      transformResponse: (response: SuccessApiResponse<INotificationsListResponse>) => response.data,
      providesTags: [GET_NOTIFICATIONS],
    }),
    deleteNotification: build.mutation<void, number>({
      query: (id) => ({
        url: `/notifications/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_NOTIFICATIONS],
    }),
    markNotificationAsRead: build.mutation<void, number>({
      query: (id) => ({
        url: `/notifications/${id}/mark-as-read`,
        method: 'PUT',
      }),
      invalidatesTags: [GET_NOTIFICATIONS],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        const patchUnread = dispatch(
          notificationsApi.util.updateQueryData('getUnreadNotificationsCount', undefined, (draft) => {
            if (draft.count > 0) {
              draft.count -= 1;
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchUnread.undo();
        }
      },
    }),
  }),
});

export const {
  useGetUnreadNotificationsCountQuery,
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useDeleteNotificationMutation,
} = notificationsApi;

export { notificationsApi };
